import { selectIsMIApplyButtonFeatureFlagOn } from 'core/userContext';
import { isEqual, xor } from 'lodash';
import { useListNextBestActionEligibleCampaigns } from 'modules/ai/nba/hooks/useNBAEligibleCampaignQuery';
import { fetchUserMappingsRequest } from 'modules/analytics/actions';
import { StyledPage } from 'modules/productAnalytics/Styled';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { addWarningToast, removeToastKey } from 'modules/toastContainer';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { GhostButton, PrimaryButton } from 'ui/button';
import { FlexCenter } from 'ui/flexSpacer';
import { Spinner } from 'ui/spinner';
import { NavTabsList, TabsGroup } from 'ui/tabsGroup';
import { pxToRem } from 'utils/convert';
import { isEmpty } from '@iterable/utils';

import {
  MainContent,
  MainWrapper,
  PageHeaderContentWrapper,
  StyledApplyContainer,
  StyledInfoBanner,
  StyledPageHeader,
  StyledTabGroupWrapper,
  StyledTabPanel,
  TabContent,
} from '../Styled';
import {
  getInsightsFiltersRequest,
  setInsightsFiltersRequest,
  updateAppliedCrossMetrics,
  updateAppliedMediums,
} from '../actions';
import {
  CampaignLimitText,
  MaxNumCampaigns,
  OpenMetrics,
  PROXIED_OPENS_BANNER_CONTENT,
  PROXIED_OPENS_DATE,
  FETCH_ERRORS as e,
} from '../constants';
import {
  selectCampaignIds,
  selectError,
  selectPreviewThumbnails,
  selectSavedViewLoading,
} from '../selectors';
import { CampaignsTable } from './CampaignsTable';
import { CrossMediumBanner } from './CrossMediumBanner';
import { CrossMediumTimeSeries } from './CrossMediumTimeSeries';
import { DefaultFiltersModal } from './DefaultFiltersModal';
import { Devices } from './Devices';
import { FilterSidePanel } from './FilterSidePanel';
import { Location } from './Location';
import { MITab } from './MITab';
import { OverviewSection } from './OverviewSection';
import { Snippets } from './Snippets';

import { useGetValidatedUrlParams } from '../hooks/useGetValidatedUrlParams';

import {
  useAppliedFiltersContext,
  useCampaignAndMetricFiltersContext,
  useTabContext,
  useTimeFiltersContext,
  useViewContext,
} from '../context';
import { useFilters } from '../hooks/useFilters';
import { useProjectDefaults } from '../hooks/useProjectDefaultView';
import { useRestData } from '../hooks/useRestData';
import { RestDataIdentifier } from '../types';
import { getHighLevelAndMedMetricsFromMetrics } from '../utils';
import { Audience } from './Audience';
import { MultiMediumEmptyState } from './MultiMediumEmptyState';
import { PageActions } from './PageActions';
import { SavedViewsAccessDeniedPage } from './SavedViews/SavedViewsAccessDeniedPage';
import { UnsavedViewPrompt } from './SavedViews/UnsavedViewPrompt';

export const MessagingInsights = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const campaignIds = useSelector(selectCampaignIds);
  const previewThumbnails = useSelector(selectPreviewThumbnails);
  const errors = useSelector(selectError);
  const isMIApplyButtonFFOn = useSelector(selectIsMIApplyButtonFeatureFlagOn);
  const isSavedViewLoading = useSelector(selectSavedViewLoading);
  /**
   * The following campaign data fetched is been used by the following tabs
   * - Campaigns
   * - Time series
   * - Snippets
   */
  const { data: campaignsData, loading: campaignsLoading } = useRestData(
    RestDataIdentifier.CAMPAIGNS_FOR_METRICS,
  );
  /**
   * Ideally we need a new endpoint that will fetch just the
   * campaign id count for a given filter instead of us fetching
   * all the campaign data to just compute the total.
   * And, fetch the campaign data just in the tabs that needs the data.
   */
  const { campaignIdsToFetchMetrics } = campaignsData;

  const [mainContentSize, setMainContentSize] = useState(960);

  // Applied filters
  const { appliedFilters, setAppliedFilters } = useAppliedFiltersContext();

  // Time-based filters
  const { dateParams, timezone, timePreset } = useTimeFiltersContext();

  // Tab management
  const {
    selectedTab,
    hideTabContentBackground,
    showDeviceTab,
    showLocationTab,
    showSnippetTab,
    showTabs: hasFiltersSelected,
    setSelectedTab,
  } = useTabContext();

  // Campaign and metric filters
  const {
    selectedMediums,
    setSelectedMediums,
    selectedCrossMetrics,
    setSelectedCrossMetrics,
    campaignFilters,
    setCampaignFilters,
    setAppliedCampaignFilters,
    isFiltersInitialized,
  } = useCampaignAndMetricFiltersContext();

  // Complete set of url query params
  const urlParams = useGetValidatedUrlParams(selectedMediums) || {};
  const [isDefaultFiltersModalOpen, setIsDefaultFiltersModalOpen] = useState(false);
  const [showFilteredProxiedOpensBanner, setShowFilteredProxiedOpensBanner] = useState(false);

  // Metrics
  const { highLevelMetrics, analyticsMetrics, metricsLoading } = useMetricsContext();

  // View
  const { viewId } = useViewContext();

  // NBA enabled campaigns
  const { data: nbaEnabledCampaignData } = useListNextBestActionEligibleCampaigns();

  const { overallMetrics, mediumMetrics } = getHighLevelAndMedMetricsFromMetrics(
    selectedCrossMetrics,
    highLevelMetrics,
    analyticsMetrics,
  );

  const total = (campaignIdsToFetchMetrics || []).length;
  const showTabs = !!total && hasFiltersSelected;

  // Warn users when metrics are calculated using a limited number of campaigns
  useEffect(() => {
    if (total >= MaxNumCampaigns) {
      const toastKey = 'campaignLimit';
      dispatch(removeToastKey(toastKey));
      dispatch(addWarningToast(CampaignLimitText, { key: toastKey }));
    }
  }, [total]);

  const saveDefaultFilters = () => {
    /**
     * currently the setInsightsFilters endpoint requires values for selectedChannel
     * and selectedMetrics, even though they are no longer relevant to the multi-medium view.
     * We therefore have placeholder values here, until the BE endpoint is updated
     */
    const postData = {
      selectedChannel: 'Email',
      selectedMetrics: [],
      dateParams,
      timePreset,
      timezone,
      selectedFilterOptions: campaignFilters, // renamed, but still need to update FE-BE handshake
      selectedCrossMetrics,
      selectedMediums,
    };
    dispatch(setInsightsFiltersRequest(JSON.stringify(postData)));
    // Clear any existing url params
    if (!isEmpty(urlParams)) {
      history.push('/messagingInsights');
    }
  };

  useEffect(() => {
    dispatch(fetchUserMappingsRequest());
    dispatch(getInsightsFiltersRequest());
  }, []);

  const applyFilters = () => {
    setAppliedFilters({
      mediums: selectedMediums,
      campaignFilters,
      overallMetrics,
      mediumMetrics: [...mediumMetrics],
      appliedCrossMetrics: selectedCrossMetrics,
    });
  };

  /**
   * Updates the context for applied campaign filters and the Redux state
   * for applied cross metrics and applied mediums whenever the user updates
   * the applied filters.
   */
  useEffect(() => {
    if (isMIApplyButtonFFOn) {
      setAppliedCampaignFilters(appliedFilters?.campaignFilters);
      dispatch(updateAppliedCrossMetrics(selectedCrossMetrics));
      dispatch(updateAppliedMediums(selectedMediums));
    }
  }, [appliedFilters]);

  const cancelChanges = () => {
    setSelectedMediums(appliedFilters?.mediums);
    setSelectedCrossMetrics(appliedFilters?.appliedCrossMetrics);
    setCampaignFilters(appliedFilters?.campaignFilters);
  };

  /**
   * Apply a user's selections on the page (e.g. default filters, settings via a saved view,
   * or filters passed in via the url)
   */
  useProjectDefaults();
  useFilters(urlParams);

  const showPermissionDenied = viewId && !isSavedViewLoading && errors[e.FETCH_SAVED_VIEW] === 403;

  const mainContentRef = useRef();

  useEffect(() => {
    setMainContentSize(mainContentRef?.current?.clientHeight || 960);
  }, [mainContentRef]);

  const isDataReady = !(campaignsLoading ?? true) && !metricsLoading && isFiltersInitialized;

  useEffect(() => {
    const isEmailSelected = selectedMediums.includes('Email');

    const hasOpenMetric = selectedCrossMetrics.some((metric) => OpenMetrics.includes(metric));

    const isFilterProxiedOpenDateInRange =
      dateParams.startTime >= PROXIED_OPENS_DATE || PROXIED_OPENS_DATE < dateParams.endTime;

    setShowFilteredProxiedOpensBanner(
      isFilterProxiedOpenDateInRange && isEmailSelected && hasOpenMetric,
    );
  }, [dateParams, selectedMediums, selectedCrossMetrics]);

  const shouldShowApplyButtons =
    !isEmpty(appliedFilters) &&
    (xor(appliedFilters?.mediums, selectedMediums).length > 0 ||
      xor(appliedFilters?.appliedCrossMetrics, selectedCrossMetrics).length > 0 ||
      !isEqual(appliedFilters?.campaignFilters, campaignFilters));

  if (showPermissionDenied) {
    return <SavedViewsAccessDeniedPage />;
  }

  return (
    <StyledPage>
      <UnsavedViewPrompt />
      <StyledPageHeader
        documentTitle={'Messaging Insights'}
        pageActions={
          <PageActions
            totalCampaigns={total}
            totalSelectedCrossMetrics={selectedCrossMetrics?.length}
            totalSelectedMediums={selectedMediums?.length}
          />
        }
      >
        <PageHeaderContentWrapper>
          <span>Messaging Insights</span>
        </PageHeaderContentWrapper>
      </StyledPageHeader>

      <CrossMediumBanner
        selectedMediums={isMIApplyButtonFFOn ? appliedFilters?.mediums : selectedMediums}
        totalCampaigns={total}
      />
      <MainWrapper>
        <FilterSidePanel
          mainContentSize={mainContentSize}
          showApplyButtons={isMIApplyButtonFFOn && shouldShowApplyButtons}
        >
          {isMIApplyButtonFFOn && shouldShowApplyButtons && (
            <StyledApplyContainer>
              <GhostButton onClick={cancelChanges}>Cancel</GhostButton>
              <PrimaryButton onClick={applyFilters}>Apply filters</PrimaryButton>
            </StyledApplyContainer>
          )}
        </FilterSidePanel>
        <MainContent ref={mainContentRef}>
          <StyledTabGroupWrapper>
            <TabsGroup value={selectedTab} onValueChange={setSelectedTab}>
              <NavTabsList>
                <MITab disabled={!showTabs} value="overview" selectedVal={selectedTab}>
                  Overview
                </MITab>
                <MITab disabled={!showTabs} value="campaigns" selectedVal={selectedTab}>
                  Campaigns
                </MITab>
                <MITab disabled={!showTabs} value="segments" selectedVal={selectedTab}>
                  Audience
                </MITab>
                <MITab disabled={!showTabs} value="timeSeries" selectedVal={selectedTab}>
                  Time series
                </MITab>
                {showLocationTab && (
                  <MITab disabled={!showTabs} value="location" selectedVal={selectedTab}>
                    Locations
                  </MITab>
                )}
                {showDeviceTab && (
                  <MITab disabled={!showTabs} value="devices" selectedVal={selectedTab}>
                    Devices
                  </MITab>
                )}
                {showSnippetTab && (
                  <MITab disabled={!showTabs} value="snippets" selectedVal={selectedTab}>
                    Snippets
                  </MITab>
                )}
              </NavTabsList>
            </TabsGroup>
          </StyledTabGroupWrapper>

          {showFilteredProxiedOpensBanner && (
            <StyledInfoBanner onClose={() => setShowFilteredProxiedOpensBanner(false)}>
              {PROXIED_OPENS_BANNER_CONTENT}
            </StyledInfoBanner>
          )}
          {showTabs && (
            <TabContent hideTabContentBackground={hideTabContentBackground}>
              {selectedTab === 'overview' && (
                <StyledTabPanel role="tabpanel" aria-labelledby={selectedTab}>
                  <OverviewSection
                    showFilteredProxiedOpensTooltips={showFilteredProxiedOpensBanner}
                  />
                </StyledTabPanel>
              )}
              {selectedTab === 'campaigns' && (
                <StyledTabPanel role="tabpanel" aria-labelledby={selectedTab}>
                  <CampaignsTable
                    previewThumbnails={previewThumbnails}
                    nbaEnabledCampaignIds={
                      nbaEnabledCampaignData?.listNextBestActionEligibleCampaigns?.campaignIds || []
                    }
                  />
                </StyledTabPanel>
              )}
              {selectedTab === 'segments' && (
                <StyledTabPanel role="tabpanel" aria-labelledby={selectedTab}>
                  <Audience campaignIds={campaignIds} />
                </StyledTabPanel>
              )}
              {selectedTab === 'timeSeries' && (
                <StyledTabPanel role="tabpanel" aria-labelledby={selectedTab}>
                  <CrossMediumTimeSeries />
                </StyledTabPanel>
              )}
              {selectedTab === 'location' && (
                <StyledTabPanel role="tabpanel" aria-labelledby={selectedTab}>
                  <Location />
                </StyledTabPanel>
              )}
              {selectedTab === 'devices' && (
                <StyledTabPanel role="tabpanel" aria-labelledby={selectedTab}>
                  <Devices />
                </StyledTabPanel>
              )}
              {selectedTab === 'snippets' && (
                <StyledTabPanel role="tabpanel" aria-labelledby={selectedTab}>
                  <Snippets />
                </StyledTabPanel>
              )}
            </TabContent>
          )}
          {isDataReady && !showTabs && (
            <MultiMediumEmptyState
              totalCampaigns={total}
              totalSelectedCrossMetrics={selectedCrossMetrics?.length}
              totalSelectedMediums={selectedMediums?.length}
            />
          )}
          {!isDataReady && !showTabs && (
            <FlexCenter height={`calc(100vh - ${pxToRem(367)})`}>
              <Spinner />
            </FlexCenter>
          )}
        </MainContent>
      </MainWrapper>
      {isDefaultFiltersModalOpen && (
        <DefaultFiltersModal
          isDefaultFiltersModalOpen={isDefaultFiltersModalOpen}
          setIsDefaultFiltersModalOpen={setIsDefaultFiltersModalOpen}
          saveDefaultFilters={saveDefaultFilters}
        />
      )}
    </StyledPage>
  );
};

export default MessagingInsights;
